/**
 * PackSelector -- Volume discount tier selector shown on the PDP when a product
 * has 2+ tiers returned by storefront-bff pricing. This is the "WoW Effect 1" UI component.
 *
 * Each tier is presented as a selectable card showing:
 *   - Pack label ("1 Pack", "2 Pack", "3 Pack")
 *   - Per-unit price (with strikethrough compare-at when present)
 *   - Savings badge (when compare-at price is higher)
 *   - Optional label ("Most Popular", "Best Value") for the highlighted tier
 *   - Total price for the pack
 *
 * Selecting a tier bumps the cart quantity to the tier's min_quantity and
 * forwards the new unit price via `onTierChange` so the parent can update
 * the displayed price.
 */

'use client';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useEffect } from 'react';
import { PricingTier } from '@/domain/entities/product.entity';
import { useTranslation } from '@/lib/i18n/client';
import { useIntl } from '@/lib/intl';
import { formatMoney } from '@/utils/formatMoney';

// ---- Styled components ---------------------------------------------------

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.md} 0;
`;

const Heading = styled.h3`
  margin: 0 0 ${({ theme }) => theme.space.xs} 0;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TierList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.xs};
`;

const TierCard = styled.button<{ $selected: boolean; $popular: boolean }>`
  position: relative;
  display: grid;
  grid-template-columns: 24px 1fr auto;
  gap: ${({ theme }) => theme.space.sm};
  align-items: center;
  padding: ${({ theme }) => `${theme.space.sm} ${theme.space.md}`};
  background: transparent;
  border: 2px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.text.primary : theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  ${({ $popular }) =>
    $popular &&
    css`
      padding-top: 20px;
    `}

  &:hover:not(:disabled) {
    border-color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const PopularBadge = styled.span`
  position: absolute;
  top: -1px;
  left: 24px;
  padding: 2px 10px;
  background: ${({ theme }) => theme.colors.text.primary};
  color: ${({ theme }) => theme.colors.background.primary};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-bottom-left-radius: ${({ theme }) => theme.radius.small};
  border-bottom-right-radius: ${({ theme }) => theme.radius.small};
`;

const Radio = styled.span<{ $selected: boolean }>`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.text.primary : theme.colors.border.color};
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${({ $selected, theme }) =>
      $selected ? theme.colors.text.primary : 'transparent'};
  }
`;

const TierBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const TierTitle = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TierSubline = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: ${({ theme }) => theme.space.xs};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const CompareAt = styled.span`
  text-decoration: line-through;
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const SavingsBadge = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background: ${({ theme }) => theme.colors.semantic.positive.color};
  color: ${({ theme }) => theme.colors.background.primary};
  font-size: 10px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  letter-spacing: 0.04em;
  border-radius: ${({ theme }) => theme.radius.small};
  margin-left: ${({ theme }) => theme.space.xs};
`;

const TierRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
`;

const TotalPrice = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[3]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.heavy};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const TotalLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

// ---- Component -----------------------------------------------------------

export interface PackSelectorProps {
  tiers: PricingTier[];
  selectedMinQuantity: number;
  onTierChange: (tier: PricingTier) => void;
}

export function PackSelector({ tiers, selectedMinQuantity, onTierChange }: PackSelectorProps) {
  const { t } = useTranslation('pdp');
  const { countryConfig } = useIntl();

  // Pre-select the tier with a "Most Popular" label (typically the middle tier).
  // Keyed on a stable identity (tier min_quantities joined) so this re-runs
  // whenever the actual tier set changes, not just when the count changes.
  const tierKey = tiers.map((t) => t.min_quantity).join(',');
  useEffect(() => {
    if (tiers.length === 0) return;
    const popular = tiers.find((tier) => tier.label);
    if (popular && popular.min_quantity !== selectedMinQuantity) {
      onTierChange(popular);
    }
  }, [tierKey, tiers, selectedMinQuantity, onTierChange]);

  if (tiers.length === 0) return null;

  const currencyCode = countryConfig.currency.code;
  const currencySymbol = countryConfig.currency.format;
  const locale = countryConfig.locale;

  return (
    <Container>
      <Heading>{t('packSelector.heading', 'Bundle & Save')}</Heading>

      <TierList role="radiogroup" aria-label={t('packSelector.heading', 'Bundle & Save')}>
        {tiers.map((tier) => {
          const isSelected = tier.min_quantity === selectedMinQuantity;
          const isPopular = !!tier.label;
          const unitCents = tier.unit_price.display;
          const totalCents = unitCents * tier.min_quantity;

          return (
            <TierCard
              key={tier.min_quantity}
              type="button"
              role="radio"
              aria-checked={isSelected}
              $selected={isSelected}
              $popular={isPopular}
              onClick={() => onTierChange(tier)}
            >
              {isPopular && tier.label && (
                <PopularBadge>{tier.label}</PopularBadge>
              )}
              <Radio $selected={isSelected} aria-hidden />
              <TierBody>
                <TierTitle>
                  {t('packSelector.pack', '{{count}} Pack', { count: tier.min_quantity })}
                </TierTitle>
                <TierSubline>
                  <span>
                    {formatMoney(unitCents, currencyCode, locale, currencySymbol)}{' '}
                    {t('packSelector.perItem', 'each')}
                  </span>
                  {tier.compare_at_price && tier.savings_percent > 0 && (
                    <>
                      <CompareAt>
                        {formatMoney(
                          tier.compare_at_price.display,
                          currencyCode,
                          locale,
                          currencySymbol,
                        )}
                      </CompareAt>
                      <SavingsBadge>
                        {t('packSelector.save', 'SAVE {{percent}}%', { percent: tier.savings_percent })}
                      </SavingsBadge>
                    </>
                  )}
                </TierSubline>
              </TierBody>
              <TierRight>
                <TotalPrice>
                  {formatMoney(totalCents, currencyCode, locale, currencySymbol)}
                </TotalPrice>
                <TotalLabel>{t('packSelector.total', 'Total')}</TotalLabel>
              </TierRight>
            </TierCard>
          );
        })}
      </TierList>
    </Container>
  );
}
